import { convertFromHTML, convertToHTML } from 'draft-convert';
import { useCallback, useRef, useState } from 'react';

import { request } from 'app/utils';
import clsx from 'clsx';
import { Box, Button, Flex, Pace, Typography } from 'components';
import { Editor as DraftEditor, EditorState, getDefaultKeyBinding } from 'draft-js';
import { useSchool } from 'features/school';
import { useParams } from 'react-router-dom';
import { VideoNotesEditorStyle } from './styles';

interface VideoNotesProps {
  name?: string;
  notes?: string;
}

const VideoNotes = ({ name, notes }: VideoNotesProps) => {
  const { productClassUuid, contentUuid } = useParams();

  const [focused, setFocused] = useState(false);
  const [edited, setEdited] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editorState, setEditorState] = useState(notes ? EditorState.createWithContent(convertFromHTML(notes)) : EditorState.createEmpty());

  const editor = useRef<DraftEditor>(null);

  const handleChangeEditor = (editorState: EditorState) => setEditorState(editorState);

  const { apiGatewayUrl } = useSchool();

  const handleCloseEditor = useCallback(() => {
    setFocused(false);

    editor?.current?.blur();
  }, [editor, editorState]);

  const handleFocusEditor = useCallback(() => {
    setFocused(true);
  }, [editor]);

  const handleBlurEditor = useCallback(() => {
    setFocused(false);
    handleSave();
  }, [edited, editorState, productClassUuid, contentUuid]);

  const handleSave = useCallback(async () => {
    if (!edited) return;

    setSaving(true);
    setEdited(false);

    const commentHtml = convertToHTML(editorState.getCurrentContent());

    request({
      url: `/api/v1/School/AssessContentNotes`,
      method: 'POST',
      baseURL: apiGatewayUrl,
      data: {
        productClassUuid,
        contentUuid,
        notes: commentHtml,
      },
    })
      .catch(() => {
        setEdited(true);
        setTimeout(handleSave, 1500);
      })
      .finally(() => {
        setSaving(false);
      });
  }, [edited, editorState, productClassUuid, contentUuid]);

  const myKeyBindingFn = useCallback(
    (e: any) => {
      if (e.key === 'Enter' && e.metaKey) {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        setTimeout(handleCloseEditor, 10);
      } else {
        setEdited(true);
      }

      return getDefaultKeyBinding(e);
    },
    [editorState]
  );

  return (
    <>
      <Flex
        className={clsx('notes-editor', { focused: focused })}
        flexGrow={1}
        position="relative"
        bg="commentsBg"
        py={{ m: 6, d: 8 }}
        px={{ m: 4, d: 10 }}
        style={{ gap: 10 }}
        flexDirection="column"
      >
        <Flex flexGrow={0} flexDirection="column" style={{ gap: 2 }} mb={2}>
          <Typography type="type1" color="color50" upper>
            Anotações
          </Typography>

          <Typography type="type5" fontWeight="bold" color="color100">
            {name}
          </Typography>
        </Flex>

        <Box position="relative">
          {saving && <Pace style={{ top: 0, right: 0 }} />}

          <DraftEditor
            ref={editor}
            editorState={editorState}
            keyBindingFn={myKeyBindingFn}
            onFocus={handleFocusEditor}
            onBlur={handleBlurEditor}
            onChange={handleChangeEditor}
          />
        </Box>

        <Flex>
          <Button onClick={handleSave} size="small">
            Salvar
          </Button>
        </Flex>
      </Flex>

      <VideoNotesEditorStyle />
    </>
  );
};

export default VideoNotes;
